import { Outlet } from '@remix-run/react'
import { RestaurantArea } from '~/components/area/restaurantArea.frag'
import { RestaurantBreadcrumbs } from '~/components/breadcrumbs/RestaurantBreadcrumbs.frag'
import { PcFooter } from '~/components/footer/pc'
import { RestaurantGenre } from '~/components/genre/restaurantGenre.frag'
import { PcHeader } from '~/components/header/pc/PcHeader'
import { Backdrop } from '~/components/modal/Backdrop'
import { RestaurantCalendar } from '~/features/calendar/RestaurantCalendar.frag'
import { CouponBanner } from '~/features/coupon/CouponBanner'
import { RestaurantSummary } from '~/features/guide/RestaurantSummary.frag'
import type { RestaurantSummaryType } from '~/features/guide/getRestaurantSummary.graphql'
import { RestaurantAreaReviewRankingFragment } from '~/features/guide/restaurantAreaReviewRanking.frag'
import { RestaurantRating } from '~/features/guide/restaurantRating.frag'
import { GuideReviewSummary } from '~/features/guide/sd/GuideReviewSummary'
import { PcGuideTitleImage } from '~/features/photos/GuideTitleImage'
import { ImageThumbnail } from '~/features/photos/pc/ImageThumbnail'
import { PcReserve } from '~/features/reserve/Reserve'
import { buildRestaurantBreadcrumbs } from '~/lib/breadcrumbs/breadcrumbs'
import { getFragmentData } from '~/lib/graphql'
import type { FragmentType } from '~/lib/graphql'
import type { RestaurantId } from '~/lib/graphql/scalars'
import { css } from '~/styled-system/css'
import { vstack } from '~/styled-system/patterns'
import { Breadcrumbs } from '~/ui/Breadcrumbs'
import { Announcement } from '../Announcement'
import { GuideNav } from '../GuideNav'
import { GuideTitle } from '../GuideTitle'
import { Introduction } from '../Introduction'
import { IkyuOnlyKiwaminoArticles } from '../KiwaminoArticles'
import { NearbySpots } from '../NearbySpots'
import SimilarRestaurants from '../SimilarRestaurants'
import { IkyuOnlySupremeRestaurantBanner } from '../SupremeRestaurantBanner'
import { IkyuOnlyOtoriyoseProducts } from '../otoriyose/OtoriyoseProducts'
import { NearbyRestaurants } from '../sd/NearbyRestaurants'
import { RecommendRestaurants } from '../sd/RecommendRestaurants'
import { CourseCalendar } from './CourseCalendar'
import { GuideRestaurantInfo } from './GuideRestaurantInfo'
import { Highlights } from './Highlights'
import { PpgOnlyPolicies } from './Policies'
import { RestaurantFaq } from './RestaurantFaq'

type Props = {
  restaurantId: RestaurantId
  restaurant: RestaurantSummaryType & FragmentType<typeof RestaurantBreadcrumbs>
  showIkyuDayBanner: boolean
}

const contents = vstack({
  paddingTop: '10',
  spaceY: '[80px]',
})

const courseCalendarHeader = css({
  spaceY: '5',
})

const introductionContainer = css({
  spaceY: '10',
  _empty: {
    display: 'none',
  },
})

const bottomContents = vstack({
  gap: '10',
  alignItems: 'start',
  w: 'content.base',
  mx: 'auto',
})

export function PCGuideRoot({ restaurantId, restaurant, showIkyuDayBanner }: Props) {
  const breadcrumbsData = getFragmentData(RestaurantBreadcrumbs, restaurant)
  const breadcrumbs = buildRestaurantBreadcrumbs(
    breadcrumbsData.name,
    restaurantId,
    breadcrumbsData.area,
    breadcrumbsData.genre,
    // まだ PC リストが res2 ハードナビゲーションで遷移する必要がある
    // TODO: PCリスト対応時に clientNavigation を有効にする
    false
  )

  const restaurantSummary = getFragmentData(RestaurantSummary, restaurant)
  const ratingInfo = getFragmentData(RestaurantRating, restaurantSummary)
  const areaReviewRanking = getFragmentData(
    RestaurantAreaReviewRankingFragment,
    restaurantSummary.areaReviewRanking
  )
  const area = getFragmentData(RestaurantArea, restaurantSummary)
  const genre = getFragmentData(RestaurantGenre, restaurantSummary)
  const { phoneNumber } = getFragmentData(RestaurantCalendar, restaurant)

  const visibleTimeSaleLabel = restaurant.searchCourses.nodes
    .flatMap((course) => course.searchPlans.nodes)
    .some((plan) => plan.timesale?.visibleLabel)

  return (
    <div itemScope itemType="https://schema.org/Restaurant">
      <Backdrop>
        <PcHeader />
        <div className={css({ width: 'content.base', margin: 'auto' })}>
          <Breadcrumbs breadcrumbs={breadcrumbs} />
        </div>
        <CouponBanner restaurantId={restaurantId} size="wide" />
        <PcGuideTitleImage restaurant={restaurant} />
        <GuideTitle
          restaurant={restaurant}
          visibleTimeSaleLabel={visibleTimeSaleLabel}
          showIkyuDayBanner={showIkyuDayBanner}
          showInventoryNotification={restaurantSummary.supportsInventoryNotification}
        />
        <GuideNav restaurantId={restaurantId} rating={ratingInfo.rating} restaurant={restaurant} />
        <div className={contents}>
          <CourseCalendar
            header={
              <div className={courseCalendarHeader}>
                <GuideReviewSummary
                  rating={ratingInfo.rating}
                  areaReviewRanking={areaReviewRanking}
                  restaurantId={restaurantId as RestaurantId}
                  isReviewHidden={restaurantSummary.isReviewHidden}
                />
                <div className={introductionContainer}>
                  <Introduction restaurant={restaurant} />
                  <Highlights restaurant={restaurant} />
                  <ImageThumbnail restaurantId={restaurantId} restaurant={restaurant} />
                  <IkyuOnlyKiwaminoArticles />
                </div>
              </div>
            }
            footer={
              <>
                <Announcement restaurant={restaurant} />
                <GuideRestaurantInfo restaurant={restaurant} />
                <RestaurantFaq restaurant={restaurant} />
                <PpgOnlyPolicies restaurantId={restaurantId} />
              </>
            }
            showInventoryNotification={restaurantSummary.supportsInventoryNotification}
            showIkyuDayBalloon={showIkyuDayBanner}
            phoneNumber={phoneNumber}
          />
          <div className={bottomContents}>
            <IkyuOnlyOtoriyoseProducts />
            <IkyuOnlySupremeRestaurantBanner restaurant={restaurant} size="wide" />
            <RecommendRestaurants restaurantId={restaurantId} />
            <NearbyRestaurants
              area={area}
              restaurant={restaurant}
              isLinkedToList
              // key を設定している理由は上記コメント参照
              key={`nearby-restaurants-${restaurantId}`}
            />
            <SimilarRestaurants area={area} genre={genre} restaurant={restaurant} />
            <NearbySpots restaurant={restaurant} />
          </div>
        </div>
        <div className={css({ pt: '10' })}>
          <PcFooter />
        </div>
      </Backdrop>
      <PcReserve />
      <Outlet />
    </div>
  )
}
